import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { compact, identity, isEmpty, size } from 'lodash';
import { Tabs as EbscoTabs, ShowMore } from '@ebsco-ui/ebsco-ui';

import {
  CLASSIFICATION_TYPE,
  IDENTIFIER_TYPE,
  SearchInstanceDto,
  SectionWithTitle,
  ShowMoreButton,
} from '@app/components';
import { useMappingsContext } from '@app/contexts';
import {
  useFeatureFlag,
  useInstanceDetailsRoutePath,
  useInstanceRelationships,
} from '@app/hooks';
import { formatArrayToString, getTabsI18n } from '@app/utils';
import {
  generalNoteTypeMessages,
  langMessages,
  restrictionOnAccessModeNote,
  secondaryNoteTypesMessages,
  sharedMessages,
} from '@app/translations';
import { FEATURE } from '@app/constants';

import {
  formatClassifications,
  formatElectronicAccess,
  formatNotes,
  formatSiblingInstancesList,
  getMarcDownloadFormats,
  getNotesData,
  getPublishers,
  renderMetadataList,
} from './utils';
import { QuickMarcView } from './QuickMarcView';
import { MetadataItem, RecordMetadata } from './RecordMetadata';

export enum INSTANCE_DETAILS_TABS {
  fullRecord,
  marcRecord,
}

interface InstanceDetailsTabsProps {
  instance: SearchInstanceDto;
  onTabChange: (value: INSTANCE_DETAILS_TABS) => void;
}

const childInstancesLimit = 5;

export const Tabs: FC<InstanceDetailsTabsProps> = ({
  instance = {} as SearchInstanceDto,
  onTabChange,
}) => {
  const intl = useIntl();
  const { $t } = intl;
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [identifiers, setIdentifiers] = useState<MetadataItem[]>([]);
  const { getInstanceDetailsRoutePath } = useInstanceDetailsRoutePath();
  const childInstances = useInstanceRelationships(instance?.childInstances);
  const { noteTypes } = useMappingsContext();
  const staffViewFlag = useFeatureFlag({ name: FEATURE.staffView });
  const shouldDisplayMarc =
    staffViewFlag.isActive && instance?.source?.includes('MARC');

  const instanceMetadataElements = useMemo(
    () => [
      {
        title: $t({
          id: 'instanceDetails.onlineAccess',
          defaultMessage: 'Online access',
        }),
        getData: instance => instance.electronicAccess,
        formatter: formatElectronicAccess,
      },
      {
        title: $t(sharedMessages.language),
        getData: instance => {
          const filteredLanguage = instance.languages?.flatMap(language =>
            langMessages[language] ? [$t(langMessages[language])] : []
          );

          return !isEmpty(filteredLanguage)
            ? filteredLanguage
            : [$t(langMessages.undefined)];
        },
        formatter: formatArrayToString,
      },
      {
        testId: 'notes',
        getData: instance =>
          getNotesData(instance, generalNoteTypeMessages, noteTypes),
        formatter: data => formatNotes(generalNoteTypeMessages, data),
      },
      {
        title: $t(sharedMessages.title),
        getData: instance => instance.title,
      },
      {
        title: $t(
          {
            id: 'instanceDetails.otherTitles',
            defaultMessage:
              '{itemCount, plural, one {Other title} other {Other titles}}',
          },
          { itemCount: size(instance?.alternativeTitles) }
        ),
        getData: instance =>
          instance.alternativeTitles?.map(
            ({ alternativeTitle }) => alternativeTitle
          ),
        formatter: renderMetadataList,
      },
      {
        title: $t({
          id: 'instanceDetails.precedingTitle',
          defaultMessage: 'Preceding title',
        }),
        getData: instance => instance.precedingTitles,
        formatter: formatSiblingInstancesList,
      },
      {
        title: $t({
          id: 'instanceDetails.succeedingTitle',
          defaultMessage: 'Succeeding title',
        }),
        getData: instance => instance.succeedingTitles,
        formatter: formatSiblingInstancesList,
      },
      {
        title: $t({
          id: 'instanceDetails.recordsInCollection',
          defaultMessage: 'Records in the collection',
        }),
        testId: 'childInstances',
        getData: () => childInstances,
        formatter: data => (
          <ShowMore limit={childInstancesLimit}>
            <ul>
              <ShowMore.Items>
                {data.map(({ id: instanceId, title }) => (
                  <li key={instanceId}>
                    <Link
                      to={getInstanceDetailsRoutePath(instanceId)}
                      key={title}
                      className="underlinedLink"
                    >
                      {title}
                    </Link>
                  </li>
                ))}
              </ShowMore.Items>
            </ul>
            <ShowMoreButton
              showMoreLimit={childInstancesLimit}
              dataLength={data.length}
            />
          </ShowMore>
        ),
      },
      {
        title: $t({
          id: 'instanceDetails.edition',
          defaultMessage: 'Edition',
        }),
        getData: instance => instance.editions,
        formatter: formatArrayToString,
      },
      {
        title: $t(sharedMessages.series),
        getData: instance => instance.series?.map(({ value }) => value),
        formatter: renderMetadataList,
      },
      {
        title: $t(sharedMessages.publisher),
        getData: getPublishers,
        formatter: renderMetadataList,
      },
      {
        title: $t({
          id: 'instanceDetails.physicalDescription',
          defaultMessage: 'Physical description',
        }),
        getData: instance => instance.physicalDescriptions,
        formatter: formatArrayToString,
      },
      {
        title: $t({
          id: 'instanceDetails.documentType',
          defaultMessage: 'Document type',
        }),
        getData: instance => instance.sourceTypes,
        formatter: renderMetadataList,
      },
      {
        title: $t({
          id: 'instanceDetails.restrictionsOnAccess',
          defaultMessage: 'Restrictions on access',
        }),
        getData: instance =>
          instance.notes?.find(
            ({ labelKey }) => labelKey === restrictionOnAccessModeNote.labelKey
          )?.note,
        formatter: renderMetadataList,
      },
      {
        testId: 'secondaryNotes',
        getData: instance =>
          getNotesData(instance, secondaryNoteTypesMessages, noteTypes),
        formatter: data => formatNotes(secondaryNoteTypesMessages, data),
      },
      {
        title: $t({
          id: 'instanceDetails.source',
          defaultMessage: 'Source',
        }),
        getData: instance => instance.source,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [$t, childInstances, getInstanceDetailsRoutePath, noteTypes]
  );
  const instanceIdentifiers = useMemo(
    () => [
      {
        title: IDENTIFIER_TYPE.ISBN,
        getData: instance => instance.isbns,
        formatter: renderMetadataList,
      },
      {
        title: IDENTIFIER_TYPE.ISSN,
        getData: instance => instance.issns,
        formatter: renderMetadataList,
      },
      {
        title: IDENTIFIER_TYPE.LCCN,
        getData: instance => instance.lccns,
        formatter: renderMetadataList,
      },
      {
        title: IDENTIFIER_TYPE.OCLC,
        getData: instance => instance.oclcs,
        formatter: renderMetadataList,
      },
      {
        title: $t({
          id: 'instanceDetails.classificationLC',
          defaultMessage: 'Classification - LC',
        }),
        getData: instance =>
          formatClassifications(
            CLASSIFICATION_TYPE.lc,
            instance?.classifications
          ),
        formatter: renderMetadataList,
      },
      {
        title: $t({
          id: 'instanceDetails.classificationDewey',
          defaultMessage: 'Classification - Dewey',
        }),
        getData: instance =>
          formatClassifications(
            CLASSIFICATION_TYPE.dewey,
            instance?.classifications
          ),
        formatter: renderMetadataList,
      },
    ],
    [$t]
  );

  const formatInstanceMetadata = useCallback(
    (instance, elements: typeof instanceMetadataElements) =>
      elements.reduce<MetadataItem[]>(
        (newInstance, { title, formatter, testId, getData }) => {
          const data = getData(instance);

          if (!isEmpty(data)) {
            newInstance.push({
              title,
              content: (formatter ?? identity)(data),
              testId,
            });
          }

          return newInstance;
        },
        []
      ),
    []
  );

  const handleTabChange = (index: INSTANCE_DETAILS_TABS) => {
    onTabChange(index);
  };

  useEffect(() => {
    if (!isEmpty(instance)) {
      setMetadata(formatInstanceMetadata(instance, instanceMetadataElements));
      setIdentifiers(formatInstanceMetadata(instance, instanceIdentifiers));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, noteTypes, childInstances]);

  return (
    <EbscoTabs
      tabs={compact([
        {
          title: $t({
            id: 'instanceDetails.fullRecord',
            defaultMessage: 'Full record',
          }),
        },
        shouldDisplayMarc && {
          title: $t({
            id: 'instanceDetails.marcRecord',
            defaultMessage: 'MARC record',
          }),
        },
      ])}
      i18n={getTabsI18n(intl)}
      onSelect={handleTabChange}
    >
      <EbscoTabs.TabPanel>
        <SectionWithTitle
          title={$t({
            id: 'instanceDetails.details',
            defaultMessage: 'Details',
          })}
        >
          <RecordMetadata items={metadata} />
        </SectionWithTitle>
        {!isEmpty(identifiers) && (
          <SectionWithTitle
            title={$t({
              id: 'instanceDetails.identifiersAndNumbering',
              defaultMessage: 'Identifiers and numbering',
            })}
          >
            <RecordMetadata items={identifiers} />
          </SectionWithTitle>
        )}
      </EbscoTabs.TabPanel>
      {shouldDisplayMarc && (
        <EbscoTabs.TabPanel>
          <QuickMarcView
            downloadFormats={getMarcDownloadFormats(instance.lccnPermalinks)}
          />
        </EbscoTabs.TabPanel>
      )}
    </EbscoTabs>
  );
};
